import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AccountState, selectors } from '../state';
import { AccountCommand } from '../commands';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';

/**
 * Order Statuses Submit Policy
 *
 * Responsibilities:
 * - React to account/orderStatuses/save/start (isSaving false -> true) — ensure
 *   the per-account SalesAccountConfiguration exists (idempotent
 *   CreateSalesAccountConfiguration) then issue SetOrderStatuses with the draft
 *   catalog, dispatching save/success or save/error.
 *
 * Mirrors `createSkuConfigSubmitPolicy`.
 */
export const createOrderStatusesSubmitPolicy = (
  backend: ClientLibraryType,
  accountId: string
): Policy<AccountState, AccountCommand> => {
  let configCreated = false;

  const ensureConfigExists = async () => {
    if ( configCreated ) return;
    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- idempotent config-create; a conflict when the config already exists is the expected path, and the real submit that follows surfaces its own errors (TV1-2010 waiver, not a rethrow).
    try {
      await backend.commands.CreateSalesAccountConfiguration({
        id: accountId,
        body: {}
      });
    } catch {
      // Idempotent — already exists, ignore
    }
    configCreated = true;
  };

  return ({
    name: 'order-statuses-submit',

    shouldReact: ( prev, next ) => {
      // React when isSaving transitions from false to true
      return !prev.orderStatuses.isSaving && next.orderStatuses.isSaving;
    },

    onStateChange: async ({ next, ctx }) => {
      const statuses = selectors.selectOrderStatuses( next );

      try {
        await ensureConfigExists();
        await backend.commands.SetOrderStatuses({
          id: accountId,
          body: { statuses }
        });

        ctx.dispatch(
          {
            type: 'account/orderStatuses/save/success',
            payload: { statuses }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        console.error( '[account] order-statuses save error:', error );

        ctx.dispatch(
          {
            type: 'account/orderStatuses/save/error',
            payload: { error: error instanceof Error ? error.message : 'Unknown error' }
          },
          { source: 'api' }
        );

        throw error;
      }
    }
  });
};
